import aiDraftApi from '@/api/aiDraft/index.ts';
import type {
    SmsAiDraftAdapter,
    SmsAiDraftFeedbackPayload,
    SmsAiDraftGeneratePayload,
} from '@/components/sms/aiDraft/types.ts';

type LeadIdResolver = () => number | null;

const resolveLeadId = (
    leadId: number | null | undefined,
    action: 'generate' | 'update' | 'stop'
): number => {
    if (!leadId) {
        throw new Error(`Cannot ${action} an AI draft without a lead id`);
    }

    return leadId;
};

export const createSmsAiDraftApiAdapter = (
    getLeadId: LeadIdResolver = () => null
): SmsAiDraftAdapter => {
    // Capture the lead at generation start so cancel still targets it after conversation switches.
    let activeLeadId: number | null = null;

    return {
        async generate({ locale, requestId }: SmsAiDraftGeneratePayload) {
            const leadId = resolveLeadId(getLeadId(), 'generate');
            activeLeadId = leadId;

            return aiDraftApi.generateSmsDraft(leadId, { locale, requestId });
        },

        async update({ locale, requestId, text, instruction }) {
            const leadId = resolveLeadId(getLeadId(), 'update');
            activeLeadId = leadId;

            return aiDraftApi.updateSmsDraft(leadId, {
                locale,
                requestId,
                text,
                instructions: instruction,
            });
        },

        async stop(requestId: string) {
            const leadId = resolveLeadId(activeLeadId ?? getLeadId(), 'stop');

            await aiDraftApi.cancelDraft(leadId, { requestId, channel: 'sms' });
        },

        async submitFeedback(payload: SmsAiDraftFeedbackPayload) {
            const leadId = getLeadId();

            if (!leadId) {
                return;
            }

            await aiDraftApi.submitDraftFeedback(leadId, {
                interactionId: payload.interactionId,
                feedback: payload.feedback,
                reasons: payload.reasons,
                details: payload.details,
            });
        },
    };
};

export const smsAiDraftApiAdapter = createSmsAiDraftApiAdapter();
